import { useEffect, useState } from 'react';
import { nomenclatureApi } from '@/services/nomenclatureApi';
import type { NomenclatureItem } from '@/features/common/types/NomenclatureItem';
import type { ExternalCatalogue } from '@/features/common/types/ExternalCatalogue';

/** The nomenclature code seeded by V043. Renaming it here without renaming the seed empties the section. */
export const LIBRARY_EXTERNAL_CATALOGUES = 'LIBRARY_CATALOAGE_EXTERNE';

/** Reads a string attribute out of an item's metadata, answering '' rather than throwing. */
function attribute(metadata: Record<string, unknown>, key: string): string {
  const value = metadata[key];
  return typeof value === 'string' ? value.trim() : '';
}

/**
 * The catalogues a nomenclature response describes.
 *
 * Nothing is dropped here for a missing address — an item with no `url` simply carries an empty
 * one, and {@link externalCatalogueHref} is the single place that decides what may be linked. Two
 * places deciding that is how a guard ends up applied on one page and not the other.
 */
export function toCatalogues(items: NomenclatureItem[]): ExternalCatalogue[] {
  return items.map((item) => {
    let metadata: Record<string, unknown> = {};
    try {
      metadata = (JSON.parse(item.metadata || '{}') as Record<string, unknown>) ?? {};
    } catch {
      // An administrator types into this field by hand. Malformed JSON costs the catalogue its
      // address, which the href guard turns into a skipped card — not a page that fails to render.
      metadata = {};
    }
    return {
      code: item.code,
      nameRo: item.nameRo,
      nameEn: item.nameEn,
      url: attribute(metadata, 'url'),
      defaultQuery: attribute(metadata, 'defaultQuery'),
      descriptionRo: attribute(metadata, 'descriptionRo'),
      descriptionEn: attribute(metadata, 'descriptionEn') || null,
    };
  });
}

/**
 * The address to open for a catalogue, or null when it holds nothing that may go in an `href`.
 *
 * This is the guard the move to the nomenclature makes necessary. While the three catalogues were
 * a const array in the bundle their addresses were written by a developer and reviewed as code.
 * They are typed into "Configurare sistem" now, and a value that reaches an anchor's `href` is not
 * just data: `javascript:…` in that field would run on the public library page, for every
 * anonymous visitor, with no deployment involved.
 *
 * So the allowlist is positive. `new URL('javascript:alert(1)')` parses happily and reports
 * protocol `javascript:` — blocking that one scheme by name would still admit `data:` and
 * `vbscript:`. Only http and https are linked; anything else, including a relative path that would
 * otherwise resolve inside this application, is not.
 *
 * The term is substituted before parsing, not after: `{` and `}` are not legal in a URI query, so
 * the stored template is not necessarily a parseable URL. What the browser receives is the
 * substituted address, and that is what has to be checked.
 */
export function externalCatalogueHref(catalogue: ExternalCatalogue, query: string): string | null {
  const term = query.trim() || catalogue.defaultQuery;
  const address = catalogue.url.split('{query}').join(encodeURIComponent(term));
  try {
    const { protocol } = new URL(address);
    return protocol === 'http:' || protocol === 'https:' ? address : null;
  } catch {
    return null;
  }
}

/**
 * The international catalogues the library links out to (FR-021, FR-042).
 *
 * Read from the nomenclature rather than hardcoded, for the reason FR-021 gives itself: it asks for
 * integration with external libraries "e.g., FAO, Horizon Europe" — examples, not a fixed list. As
 * a const array in RegistryRoutes.tsx, the Beneficiary adding a fourth catalogue or correcting an
 * address that had moved was a code change and a deployment.
 *
 * Served by the public endpoint, because the library page is anonymous. A failure yields an empty
 * list and the section is not rendered at all: these are a way onward once the library has been
 * searched, and losing them is a smaller problem than a heading standing over nothing. There is
 * deliberately no hardcoded fallback as useMapSymbology has — a map without its legend is broken,
 * where a stale fallback list here would put back exactly the drift this removes.
 */
export function useExternalCatalogues(): ExternalCatalogue[] {
  const [catalogues, setCatalogues] = useState<ExternalCatalogue[]>([]);

  useEffect(() => {
    nomenclatureApi
      .publicItems<NomenclatureItem>(LIBRARY_EXTERNAL_CATALOGUES)
      .then((items) => setCatalogues(toCatalogues(items)))
      .catch(() => setCatalogues([]));
  }, []);

  return catalogues;
}

/** The title to show, falling back to Romanian — the tender requires RO everywhere, EN only in places. */
export function catalogueLabel(catalogue: ExternalCatalogue, language: string): string {
  return language === 'en' && catalogue.nameEn ? catalogue.nameEn : catalogue.nameRo;
}

/** The line under the title, same rule. */
export function catalogueDescription(catalogue: ExternalCatalogue, language: string): string {
  return language === 'en' && catalogue.descriptionEn
    ? catalogue.descriptionEn
    : catalogue.descriptionRo;
}
